<template>
  <div class="reports-container">
    <div class="page-header">
      <h2>检测报告管理</h2>
      <el-button type="primary" @click="showAddDialog">新增报告</el-button>
    </div>

    <!-- 搜索区域 -->
    <div class="search-section">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="车辆ID">
          <el-input v-model="searchForm.vehicleId" placeholder="请输入车辆ID" clearable></el-input>
        </el-form-item>
        <el-form-item label="检测类型">
          <el-select v-model="searchForm.type" placeholder="请选择检测类型" clearable>
            <el-option label="事故车检测" value="accident"></el-option>
            <el-option label="泡水车检测" value="water"></el-option>
            <el-option label="火烧车检测" value="fire"></el-option>
            <el-option label="电池安全隐患" value="battery_safety"></el-option>
            <el-option label="物理损伤检测" value="physical_damage"></el-option>
            <el-option label="基础功能检测" value="basic_function"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="结论">
          <el-input v-model="searchForm.conclusion" placeholder="请输入检测结论" clearable></el-input>
        </el-form-item>
        <el-form-item label="结论标题">
          <el-input v-model="searchForm.conclusionTitle" placeholder="请输入结论标题" clearable></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 报告列表 -->
    <div class="table-section">
      <el-table :data="reportList" v-loading="loading" border stripe>
        <el-table-column prop="reportId" label="报告ID" width="100"></el-table-column>
        <el-table-column prop="vehicleId" label="车辆ID" width="100"></el-table-column>
        <el-table-column prop="type" label="检测类型" width="100">
          <template slot-scope="scope">
            <el-tag :type="getTypeTagType(scope.row.type)">
              {{ getTypeLabel(scope.row.type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="检测标题" width="120"></el-table-column>
        <el-table-column prop="conclusion" label="检测结论" width="100">
          <template slot-scope="scope">
            <el-tag :type="getConclusionTagType(scope.row.conclusion)">
              {{ scope.row.conclusion }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="conclusionTitle" label="结论标题" width="120"></el-table-column>
        <el-table-column label="统计信息" width="150">
          <template slot-scope="scope">
            <div>瑕疵: {{ scope.row.defectCount }}</div>
            <div>不可检: {{ scope.row.uninspectableCount }}</div>
            <div>总计: {{ scope.row.totalCount }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="primaryStatus" label="主要状态" width="100"></el-table-column>
        <el-table-column prop="secondaryStatus" label="次要状态" width="100"></el-table-column>
        <el-table-column label="状态标识" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.hasDefect" type="danger" size="mini">有瑕疵</el-tag>
            <el-tag v-if="scope.row.hasTrace" type="warning" size="mini">有痕迹</el-tag>
            <el-tag v-if="!scope.row.hasDefect && !scope.row.hasTrace" type="success" size="mini">正常</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="showEditDialog(scope.row)">编辑</el-button>
            <el-button size="mini" type="success" @click="showDetailDialog(scope.row)">详情</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-section">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.current"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total">
        </el-pagination>
      </div>
    </div>

    <!-- 新增/编辑报告对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="800px" @close="resetForm">
      <el-form :model="reportForm" :rules="reportRules" ref="reportForm" label-width="120px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="报告ID" prop="reportId">
              <el-select v-model="reportForm.reportId" placeholder="请选择报告ID" :disabled="isEdit" style="width: 100%" @change="handleReportIdChange">
                <el-option label="R001 - 事故车检测" value="R001"></el-option>
                <el-option label="R002 - 泡水车检测" value="R002"></el-option>
                <el-option label="R003 - 火烧车检测" value="R003"></el-option>
                <el-option label="R004 - 电池安全隐患" value="R004"></el-option>
                <el-option label="R005 - 物理损伤检测" value="R005"></el-option>
                <el-option label="R006 - 基础功能检测" value="R006"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="车辆ID" prop="vehicleId">
              <el-select v-model="reportForm.vehicleId" placeholder="请选择车辆ID" style="width: 100%">
                <el-option
                  v-for="vehicle in vehicleOptions"
                  :key="vehicle.vehicleId"
                  :label="`${vehicle.vehicleId} - ${vehicle.brand} ${vehicle.model}`"
                  :value="vehicle.vehicleId"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="检测类型" prop="type">
              <el-select v-model="reportForm.type" placeholder="请选择检测类型" style="width: 100%">
                <el-option label="事故车检测" value="accident"></el-option>
                <el-option label="泡水车检测" value="water"></el-option>
                <el-option label="火烧车检测" value="fire"></el-option>
                <el-option label="电池安全隐患" value="battery_safety"></el-option>
                <el-option label="物理损伤检测" value="physical_damage"></el-option>
                <el-option label="基础功能检测" value="basic_function"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="检测标题" prop="title">
              <el-input v-model="reportForm.title" placeholder="请输入检测标题"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="检测结论" prop="conclusion">
              <el-input v-model="reportForm.conclusion" placeholder="请输入检测结论"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="结论标题" prop="conclusionTitle">
              <el-input v-model="reportForm.conclusionTitle" placeholder="请输入结论标题"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="主要状态" prop="primaryStatus">
              <el-input v-model="reportForm.primaryStatus" placeholder="请输入主要状态"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="次要状态" prop="secondaryStatus">
              <el-input v-model="reportForm.secondaryStatus" placeholder="请输入次要状态"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="瑕疵数量" prop="defectCount">
              <el-input-number v-model="reportForm.defectCount" :min="0" placeholder="瑕疵数量" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="不可检数量" prop="uninspectableCount">
              <el-input-number v-model="reportForm.uninspectableCount" :min="0" placeholder="不可检数量" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="总数量" prop="totalCount">
              <el-input-number v-model="reportForm.totalCount" :min="0" placeholder="总数量" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="是否有瑕疵" prop="hasDefect">
              <el-switch v-model="reportForm.hasDefect"></el-switch>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="是否有痕迹" prop="hasTrace">
              <el-switch v-model="reportForm.hasTrace"></el-switch>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="检测描述" prop="description">
          <el-input type="textarea" v-model="reportForm.description" :rows="4" placeholder="请输入检测描述"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
      </div>
    </el-dialog>

    <!-- 报告详情对话框 -->
    <el-dialog title="检测报告详情" :visible.sync="detailDialogVisible" width="800px">
      <div v-if="currentReport" class="report-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="报告ID">{{ currentReport.reportId }}</el-descriptions-item>
          <el-descriptions-item label="车辆ID">{{ currentReport.vehicleId }}</el-descriptions-item>
          <el-descriptions-item label="检测类型">
            <el-tag :type="getTypeTagType(currentReport.type)">
              {{ getTypeLabel(currentReport.type) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="检测标题">{{ currentReport.title }}</el-descriptions-item>
          <el-descriptions-item label="检测结论">
            <el-tag :type="getConclusionTagType(currentReport.conclusion)">
              {{ currentReport.conclusion }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="结论标题">{{ currentReport.conclusionTitle }}</el-descriptions-item>
          <el-descriptions-item label="主要状态">{{ currentReport.primaryStatus }}</el-descriptions-item>
          <el-descriptions-item label="次要状态">{{ currentReport.secondaryStatus }}</el-descriptions-item>
          <el-descriptions-item label="瑕疵数量">{{ currentReport.defectCount }}</el-descriptions-item>
          <el-descriptions-item label="不可检数量">{{ currentReport.uninspectableCount }}</el-descriptions-item>
          <el-descriptions-item label="总数量">{{ currentReport.totalCount }}</el-descriptions-item>
          <el-descriptions-item label="是否有瑕疵">
            <el-tag :type="currentReport.hasDefect ? 'danger' : 'success'">
              {{ currentReport.hasDefect ? '是' : '否' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="是否有痕迹">
            <el-tag :type="currentReport.hasTrace ? 'warning' : 'success'">
              {{ currentReport.hasTrace ? '是' : '否' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="检测描述" :span="2">{{ currentReport.description }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { reportApi } from '@/api/report'
import { vehicleApi } from '@/api/vehicle'

export default {
  name: 'Reports',
  data() {
    return {
      loading: false,
      submitLoading: false,
      reportList: [],
      searchForm: {
        vehicleId: '',
        type: '',
        conclusion: '',
        conclusionTitle: ''
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      dialogVisible: false,
      detailDialogVisible: false,
      isEdit: false,
      currentReport: null,
      reportForm: {
        reportId: '',
        vehicleId: '',
        type: '',
        title: '',
        conclusion: '',
        conclusionTitle: '',
        description: '',
        defectCount: 0,
        uninspectableCount: 0,
        totalCount: 0,
        primaryStatus: '',
        secondaryStatus: '',
        hasDefect: false,
        hasTrace: false
      },
      vehicleOptions: [], // 车辆选项列表
      reportRules: {
        reportId: [
          { required: true, message: '请选择报告ID', trigger: 'change' }
        ],
        vehicleId: [
          { required: true, message: '请选择车辆ID', trigger: 'change' }
        ],
        type: [
          { required: true, message: '请选择检测类型', trigger: 'change' }
        ],
        title: [
          { required: true, message: '请输入检测标题', trigger: 'blur' }
        ],
        conclusion: [
          { required: true, message: '请输入检测结论', trigger: 'blur' }
        ],
        conclusionTitle: [
          { required: true, message: '请输入结论标题', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑检测报告' : '新增检测报告'
    }
  },
  mounted() {
    this.loadData()
    this.loadVehicleOptions()
  },
  methods: {
    // 加载车辆选项
    async loadVehicleOptions() {
      try {
        const response = await vehicleApi.getVehicleList({ current: 1, size: 1000 })
        if (response && (response.code === 200 || response.code === '200') && response.data) {
          this.vehicleOptions = response.data.records || []
        }
      } catch (error) {
        console.error('加载车辆选项失败:', error)
        this.$message.error('加载车辆选项失败')
      }
    },

    // 加载数据
    async loadData() {
      this.loading = true
      try {
        const params = {
          current: this.pagination.current,
          size: this.pagination.size,
          ...this.searchForm
        }
        const response = await reportApi.getReportList(params)
        if (response && (response.code === 200 || response.code === '200') && response.data) {
          this.reportList = response.data.records || []
          this.pagination.total = response.data.total || 0
        }
      } catch (error) {
        console.error('加载检测报告数据失败:', error)
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        vehicleId: '',
        type: '',
        conclusion: '',
        conclusionTitle: ''
      }
      this.handleSearch()
    },

    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.size = size
      this.loadData()
    },

    // 当前页改变
    handleCurrentChange(current) {
      this.pagination.current = current
      this.loadData()
    },

    // 显示新增对话框
    showAddDialog() {
      this.isEdit = false
      this.dialogVisible = true
    },

    // 显示编辑对话框
    showEditDialog(row) {
      this.isEdit = true
      this.currentReport = row
      this.reportForm = { 
        ...row,
        // 确保布尔值字段正确转换
        hasDefect: Boolean(row.hasDefect),
        hasTrace: Boolean(row.hasTrace)
      }
      this.dialogVisible = true
    },

    // 显示详情对话框
    showDetailDialog(row) {
      this.currentReport = row
      this.detailDialogVisible = true
    },

    // 提交表单
    async handleSubmit() {
      this.$refs.reportForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true
          try {
            // 格式化提交数据
            const submitData = {
              ...this.reportForm,
              // 将布尔值转换为整数
              hasDefect: this.reportForm.hasDefect ? 1 : 0,
              hasTrace: this.reportForm.hasTrace ? 1 : 0
            }
            
            if (this.isEdit) {
              await reportApi.updateReport(submitData)
              this.$message.success('更新成功')
            } else {
              await reportApi.addReport(submitData)
              this.$message.success('添加成功')
            }
            this.dialogVisible = false
            this.loadData()
          } catch (error) {
            console.error('提交失败:', error)
            this.$message.error('操作失败')
          } finally {
            this.submitLoading = false
          }
        }
      })
    },

    // 删除报告
    handleDelete(row) {
      this.$confirm('确定要删除该检测报告吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await reportApi.deleteReport(row.reportId)
          this.$message.success('删除成功')
          this.loadData()
        } catch (error) {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },

    // 重置表单
    resetForm() {
      this.$refs.reportForm.resetFields()
      this.reportForm = {
        reportId: '',
        vehicleId: '',
        type: '',
        title: '',
        conclusion: '',
        conclusionTitle: '',
        description: '',
        defectCount: 0,
        uninspectableCount: 0,
        totalCount: 0,
        primaryStatus: '',
        secondaryStatus: '',
        hasDefect: false,
        hasTrace: false
      }
    },

    // 获取检测类型标签类型
    getTypeTagType(type) {
      const types = {
        'accident': 'danger',
        'water': 'info',
        'fire': 'warning',
        'battery_safety': 'danger',
        'physical_damage': 'warning',
        'basic_function': ''
      }
      return types[type] || 'info'
    },

    // 获取检测类型标签
    getTypeLabel(type) {
      const labels = {
        'accident': '事故车检测',
        'water': '泡水车检测',
        'fire': '火烧车检测',
        'battery_safety': '电池安全隐患',
        'physical_damage': '物理损伤检测',
        'basic_function': '基础功能检测'
      }
      return labels[type] || type
    },

    // 处理报告ID变化
    handleReportIdChange(reportId) {
      // 根据报告ID自动设置检测类型
      const typeMap = {
        'R001': 'accident',
        'R002': 'water',
        'R003': 'fire',
        'R004': 'battery_safety',
        'R005': 'physical_damage',
        'R006': 'basic_function'
      }
      if (typeMap[reportId]) {
        this.reportForm.type = typeMap[reportId]
      }
    },

    // 获取结论标签类型
    getConclusionTagType(conclusion) {
      if (conclusion.includes('非')) {
        return 'success'
      } else if (conclusion.includes('事故') || conclusion.includes('泡水') || conclusion.includes('火烧') ||
                 conclusion.includes('隐患') || conclusion.includes('损伤') || conclusion.includes('故障')) {
        return 'danger'
      }
      return 'info'
    }
  }
}
</script>

<style scoped>
.reports-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.search-section {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-form {
  margin: 0;
}

.table-section {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.pagination-section {
  padding: 20px;
  text-align: right;
}

/* 操作按钮样式优化 */
.el-button--mini {
  margin-right: 5px;
}

.el-button--mini:last-child {
  margin-right: 0;
}

/* 表格内容垂直居中 */
.el-table .cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.el-table .cell:first-child {
  justify-content: flex-start;
}

/* 操作列按钮容器 */
.el-table .el-button + .el-button {
  margin-left: 5px;
}

/* 统计信息样式 */
.statistics-info {
  font-size: 12px;
  line-height: 1.4;
}

.statistics-info div {
  margin-bottom: 2px;
}
</style>